<script lang="ts">
  /**
   * Waiting from Drive — the review step for the watched folder.
   *
   * The sweep does the tedious half: finds new files in the shared Drive folder,
   * downloads them, uploads them to the bucket, hashes them, and checks whether the
   * same bytes are already filed. What it deliberately does not do is file anything.
   * The spec is unambiguous — "No item leaves the queue without a human press" — so
   * every row here needs a person to confirm it.
   *
   * The guesses are labelled as guesses, for the same reason they are on the File
   * tab: an inferred title somebody can see was inferred gets corrected, and one
   * that looks typed gets confirmed unread.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import type { Check } from "../lib/model";

  type QueueItem = {
    id: string;
    driveName: string | null;
    status: string;
    sizeBytes: number | null;
    mime: string | null;
    guessedTitle: string;
    guessedDocType: string;
    guessedDate: string | null;
    matchesDocId: string | null;
    createdAt: string | null;
  };
  type Meta = { docTypes: string[]; expiryKinds: string[]; suggested: Record<string, string> };

  const { meta, onFiled, pretty }: { meta: Meta | null; onFiled: () => void; pretty: (s: string) => string } =
    $props();

  let items = $state<QueueItem[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);
  let syncing = $state(false);
  let busyId = $state<string | null>(null);

  /** Per-row edits, seeded from the guesses on first render. */
  let drafts = $state<Record<string, { title: string; docType: string; documentDate: string; expiresAt: string; policyNumber: string; supplier: string; inCase: boolean }>>({});

  const waiting = $derived(items.filter((i) => i.status === "waiting"));
  const dupes = $derived(items.filter((i) => i.status === "duplicate"));

  async function load() {
    loading = true;
    try {
      const r = await api.get<{ items: QueueItem[] }>("/queue");
      items = r.items;
      for (const i of r.items) {
        drafts[i.id] ??= {
          title: i.guessedTitle,
          docType: i.guessedDocType,
          documentDate: i.guessedDate ?? "",
          expiresAt: "",
          policyNumber: "",
          supplier: "",
          inCase: false,
        };
      }
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not read the queue";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  /** "Check Drive now", rather than waiting for the next scheduled sweep. */
  async function syncNow() {
    syncing = true;
    note = null;
    try {
      const r = await api.post<{
        folder: string | null;
        seen: number;
        queued: number;
        alreadyKnown: number;
        duplicateContent: number;
        failed: Array<{ name: string; why: string }>;
        skipped: string[];
        note: string | null;
      }>("/ops/drive-sync");

      // The server's own note wins — it names the sharing problem, which is the
      // cause nobody guesses from "0 files found".
      note =
        r.note ??
        `Looked at ${r.seen} file${r.seen === 1 ? "" : "s"}: ${r.queued} new, ${r.alreadyKnown} already known` +
          `${r.duplicateContent > 0 ? `, ${r.duplicateContent} already filed` : ""}` +
          `${r.skipped.length > 0 ? `, ${r.skipped.length} skipped as not documents` : ""}` +
          `${r.failed.length > 0 ? `, ${r.failed.length} could not be read` : ""}.`;
      await load();
    } catch (e) {
      note = e instanceof ApiError ? e.message : "The sweep could not run.";
    } finally {
      syncing = false;
    }
  }

  async function confirm(item: QueueItem) {
    const d = drafts[item.id];
    if (!d?.title.trim()) {
      note = "Give it a title first.";
      return;
    }
    busyId = item.id;
    try {
      const r = await api.post<{ id: string; checks: Check[] }>(`/queue/${item.id}/confirm`, {
        title: d.title,
        docType: d.docType,
        supplier: d.supplier || null,
        policyNumber: d.policyNumber || null,
        documentDate: d.documentDate || null,
        expiresAt: d.expiresAt || null,
        inCase: d.inCase,
      });
      note =
        r.checks.length > 0
          ? `Filed “${d.title}”, with ${r.checks.length} thing${r.checks.length === 1 ? "" : "s"} worth checking.`
          : `Filed “${d.title}”.`;
      items = items.filter((i) => i.id !== item.id);
      onFiled();
    } catch (e) {
      note = e instanceof ApiError ? e.message : "Could not file it.";
    } finally {
      busyId = null;
    }
  }

  async function discard(item: QueueItem) {
    busyId = item.id;
    try {
      await api.del(`/queue/${item.id}`);
      items = items.filter((i) => i.id !== item.id);
      // Said plainly, because "discard" sounds like deletion and is not: the copy in
      // the bucket stays, and so does the original in Drive.
      note = "Left out of Vault. The file itself is untouched, in Drive and in the bucket.";
    } catch (e) {
      note = e instanceof ApiError ? e.message : "Could not discard it.";
    } finally {
      busyId = null;
    }
  }

  const size = (b: number | null) =>
    b == null ? "—" : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;
</script>

<section class="card">
  <div class="sec">
    <span class="kicker">Waiting from Drive</span>
    <span class="sub">{waiting.length} to confirm</span>
  </div>

  <div class="acts acts--top">
    <button class="btn-primary" disabled={syncing} onclick={syncNow}>
      {syncing ? "Checking Drive…" : "Check Drive now"}
    </button>
  </div>

  {#if note}
    <div class="attn">
      <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
      <span class="attn__text">{note}</span>
      <button class="lnk" onclick={() => (note = null)}>Dismiss</button>
    </div>
  {/if}

  {#if loading}
    <p class="sub pad">Loading…</p>
  {:else if error}
    <p class="sub pad"><Icon name="alert" size={12} /> {error}</p>
  {:else if waiting.length === 0 && dupes.length === 0}
    <p class="sub pad">
      <Icon name="pause" size={12} />
      Nothing waiting. Drop documents into the shared Drive folder and they appear here within ten minutes.
    </p>
  {/if}

  {#each waiting as item (item.id)}
    {@const d = drafts[item.id]}
    <article class="row">
      <div class="row__top">
        <span class="row__name">{item.driveName}</span>
        <span class="sub">{size(item.sizeBytes)} · from Drive</span>
      </div>

      {#if d}
        <div class="grid">
          <label class="wide">
            <span class="lb">What is it <span class="sub">· guessed from the filename</span></span>
            <input bind:value={d.title} />
          </label>
          <label>
            <span class="lb">Type <span class="sub">· guessed</span></span>
            <select bind:value={d.docType}>
              {#each meta?.docTypes ?? [] as t (t)}<option value={t}>{pretty(t)}</option>{/each}
            </select>
          </label>
          <label>
            <span class="lb">Supplier or issuer</span>
            <input bind:value={d.supplier} />
          </label>
          <label>
            <span class="lb">Policy number <span class="sub">(or account)</span></span>
            <input bind:value={d.policyNumber} autocomplete="off" spellcheck="false" />
          </label>
          <label>
            <span class="lb">
              Date on it{#if item.guessedDate}<span class="sub"> · guessed</span>{/if}
            </span>
            <input bind:value={d.documentDate} type="date" />
          </label>
          <label>
            <span class="lb">Expires <span class="sub">(blank if none)</span></span>
            <input bind:value={d.expiresAt} type="date" />
          </label>
          <label class="tog wide">
            <input type="checkbox" bind:checked={d.inCase} />
            <span>Put it in the <strong>in case</strong> folder</span>
          </label>
        </div>

        <div class="acts">
          <button class="lnk" disabled={busyId === item.id} onclick={() => discard(item)}>Leave it out</button>
          <button class="btn-primary" disabled={busyId === item.id} onclick={() => confirm(item)}>
            {busyId === item.id ? "Filing…" : "File it"}
          </button>
        </div>
      {/if}
    </article>
  {/each}

  {#if dupes.length > 0}
    <!-- Shown rather than hidden: "already in Vault" is worth seeing once, and
         swallowing it looks like the sweep having missed the file. -->
    <div class="checks">
      <div class="sub colhead">Already filed, skipped</div>
      {#each dupes as item (item.id)}
        <div class="sub check"><Icon name="check" size={12} /> {item.driveName} — the same file is already in Vault.</div>
      {/each}
    </div>
  {/if}
</section>

<style>
  .row {
    padding: 0.8rem 0;
    border-bottom: 1px solid var(--line);
  }
  .row__top {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }
  .row__name {
    font-weight: 700;
    word-break: break-all;
  }
  .acts--top {
    justify-content: flex-start;
    padding-top: 0;
    padding-bottom: 0.6rem;
  }
</style>
